/**
 * Formats error messages that may contain long lists of names (e.g., professionals)
 * by truncating and showing "ver mais" for expanded view
 */

const MAX_VISIBLE_NAMES = 3;

/**
 * Escapes HTML special characters to prevent XSS attacks
 */
function escapeHtml(text: string): string {
	const div = document.createElement('div');
	div.textContent = text;
	return div.innerHTML;
}

/**
 * Extracts comma-separated names from error messages
 * Expects format: "...profissionais: Name1, Name2, Name3..."
 */
function extractNamesFromMessage(message: string): string[] {
	const listMatch = message.match(/profissionais:\s*(.+?)(?:\.|$)/i);
	if (listMatch) {
		return listMatch[1].split(',').map(name => name.trim()).filter(name => name.length > 0);
	}
	return [];
}

/**
 * Shows an expandable toast error with "ver mais" functionality
 * Allows users to click to see all names in long error messages
 */
export function showExpandableToast(toasted: any, error: any, defaultMessage?: string): void {
	if (!toasted || !toasted.error) return;

	const message = error?.message || error?.friendlyMessage || defaultMessage || "Ocorreu um erro";
	const allNames = extractNamesFromMessage(message);

	// If 3 or fewer names, show regular toast without expansion
	if (allNames.length <= MAX_VISIBLE_NAMES) {
		toasted.error(message, { duration: 5000 });
		return;
	}

	// Extract prefix (e.g., "Erro na conta bancária dos seguintes profissionais:")
	const prefixMatch = message.match(/^(.+?profissionais:)\s*/i);
	const prefix = prefixMatch ? prefixMatch[1] : 'Erro:';
	const visibleNames = allNames.slice(0, MAX_VISIBLE_NAMES);

	// Create unique ID for this toast
	const toastId = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
	let isExpanded = false;
	let clickHandler: ((e: Event) => void) | null = null;

	// Build HTML for collapsed or expanded state
	const buildHtml = (expanded: boolean): string => {
		if (expanded) {
			// Sanitize names to prevent XSS
			const namesList = allNames.map(name => `<div style="margin-left: 20px;">• ${escapeHtml(name)}</div>`).join('');
			return `<div id="${toastId}">
				<div>${escapeHtml(prefix)}</div>
				${namesList}
				<div style="margin-top: 8px;">
					<a href="#" class="toast-ver-mais" style="color: #fff; text-decoration: underline; font-weight: bold; cursor: pointer;">ver menos</a>
				</div>
			</div>`;
		} else {
			// Sanitize names to prevent XSS
			const sanitizedNames = visibleNames.map(name => escapeHtml(name)).join(', ');
			const truncatedList = `${escapeHtml(prefix)} ${sanitizedNames} ... <a href="#" class="toast-ver-mais" style="color: #fff; text-decoration: underline; font-weight: bold; cursor: pointer;">ver mais</a>`;
			return `<div id="${toastId}">${truncatedList}</div>`;
		}
	};

	// Show initial toast
	toasted.error(buildHtml(false), {
		duration: 10000,
		action: null,
	});

	// Add click handler after toast is rendered
	setTimeout(() => {
		const toastElement = document.getElementById(toastId);
		if (!toastElement) return;

		clickHandler = (e: Event) => {
			const target = e.target as HTMLElement;
			if (target.classList.contains('toast-ver-mais')) {
				e.preventDefault();
				isExpanded = !isExpanded;
				
				const contentDiv = document.getElementById(toastId);
				if (contentDiv) {
					// Replace only the inner content, preserving the wrapper div with ID
					const newHtml = buildHtml(isExpanded);
					const tempDiv = document.createElement('div');
					tempDiv.innerHTML = newHtml;
					const newContent = tempDiv.firstElementChild;
					if (newContent) {
						contentDiv.innerHTML = newContent.innerHTML;
					}
				}
			}
		};

		toastElement.addEventListener('click', clickHandler);

		// Cleanup: remove event listener when toast is removed from DOM
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.removedNodes.forEach((node) => {
					if (node === toastElement && clickHandler) {
						toastElement.removeEventListener('click', clickHandler);
						observer.disconnect();
					}
				});
			});
		});

		if (toastElement.parentNode) {
			observer.observe(toastElement.parentNode, { childList: true });
		}
	}, 150);
}
